import type { Entrega } from '../../api/ops/clientes';
import type { Comision, Invitado, Liquidacion, VentaAtribuida } from '../../api/ops/portalRrpp';
import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';

/**
 * Lógica sin estilos del **portal del RRPP** (10.12): secciones, textos de cada invitado, del último envío, de las ventas,
 * comisiones y liquidaciones. Nada de cifras inventadas ni importes calculados: todo sale de la API tal cual.
 */
/** Permisos que mira el portal (solo ocultan: la API protege cada petición). */
export const PERMISOS_PORTAL = {
    listas: 'listas.listas.ver',
    invitados: 'listas.invitados.ver',
    apuntar: 'listas.invitados.gestionar',
    enlaces: 'listas.enlaces.ver',
    gestionarEnlaces: 'listas.enlaces.gestionar',
    comisiones: 'rrpp.comisiones.ver',
    liquidaciones: 'rrpp.liquidaciones.ver',
    registro: 'entregas.registro.ver',
    reenviar: 'entregas.entregas.reenviar',
} as const;

export type Seccion = 'inicio' | 'eventos' | 'invitados' | 'ventas' | 'comisiones';

export const SECCIONES: readonly { valor: Seccion; etiqueta: string; corta?: string; icono: NombreIcono }[] = [
    { valor: 'inicio', etiqueta: 'Inicio', icono: 'house' },
    { valor: 'eventos', etiqueta: 'Eventos', icono: 'calendar' },
    { valor: 'invitados', etiqueta: 'Invitados', icono: 'users' },
    { valor: 'ventas', etiqueta: 'Ventas', icono: 'ticket' },
    { valor: 'comisiones', etiqueta: 'Comisiones', corta: 'Comisión', icono: 'percent' },
];

export const SUBTITULOS: Readonly<Record<Seccion, string>> = {
    inicio: 'Lo tuyo en este negocio: tus listas, tus enlaces y tus avisos.',
    eventos: 'Tus eventos, tus listas con su cupo y tus enlaces para compartir.',
    invitados: 'Quien has apuntado tú y quien se ha apuntado con tus enlaces.',
    ventas: 'Lo que se vende con tu enlace o tu código: entradas y reservados.',
    comisiones: 'Lo que llevas ganado y tus liquidaciones cerradas.',
};

/** `?seccion=` de la dirección; `?invitado=` abre Invitados (el «Ver» del aviso `listas.pase_no_entregado`, G791). */
export function seccionDe(busqueda: URLSearchParams): Seccion {
    if (busqueda.get('invitado') !== null) {
        return 'invitados';
    }
    const s = busqueda.get('seccion');
    return SECCIONES.some((x) => x.valor === s) ? (s as Seccion) : 'inicio';
}

/** La dirección se enseña sin `https://` (G125). */
export function sinProtocolo(url: string): string {
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

/** «Marta Gil Ruiz +1». */
export function nombreConAcompanantes(i: Pick<Invitado, 'display_name' | 'companions'>): string {
    return i.companions > 0 ? `${i.display_name} +${String(i.companions)}` : i.display_name;
}

const CANAL: Readonly<Record<Invitado['contact_channel'], readonly [string, NombreIcono]>> = {
    instagram: ['Instagram', 'instagram'],
    email: ['Email', 'mail'],
    whatsapp: ['WhatsApp', 'message-circle'],
};

const ORIGEN: Readonly<Record<Invitado['origin'], string>> = {
    promoter: 'Lo apuntaste tú',
    staff: 'Lo apuntó el negocio',
    generic_link: 'Con tu enlace de Instagram',
    web_form: 'Con tu formulario web',
};

export function canalDe(i: Pick<Invitado, 'contact_channel'>): { texto: string; icono: NombreIcono } {
    const [texto, icono] = CANAL[i.contact_channel];
    return { texto, icono };
}

/** «Instagram · Lo apuntaste tú», con «Salió él mismo» delante si se quitó con su enlace (G755). */
export function lineaInvitado(i: Pick<Invitado, 'contact_channel' | 'origin' | 'withdrawn_by_guest' | 'status'>): string {
    const partes = [CANAL[i.contact_channel][0], ORIGEN[i.origin]];
    if (i.status === 'withdrawn' && i.withdrawn_by_guest) {
        partes.unshift('Salió él mismo');
    }
    return partes.join(' · ');
}

/** Estado del invitado en la lista: «Retirado», «Cancelado», «Pase enviado» o «Apuntado» (le falta escribir por Instagram). */
export function estadoInvitado(i: Pick<Invitado, 'status' | 'pass_state' | 'pass_delivered_at' | 'arrived_at'>): { texto: string; chip: EstadoChip | null } {
    if (i.status === 'withdrawn') {
        return { texto: 'Retirado', chip: 'problem' };
    }
    if (i.status === 'cancelled') {
        return { texto: 'Cancelado', chip: 'problem' };
    }
    if (i.arrived_at != null) {
        return { texto: 'Ha llegado', chip: 'confirmed' };
    }
    if (i.pass_state === 'waiting_instagram') {
        return { texto: 'Apuntado', chip: 'neutral' };
    }
    if (i.pass_state === 'delivered' || i.pass_delivered_at != null) {
        return { texto: 'Pase enviado', chip: null };
    }
    return { texto: 'Apuntado', chip: 'neutral' };
}

/** «Su último envío»: canal y si llegó («entregado», «leído», «no ha llegado»), con la lectura de 5.5. */
export function ultimoEnvio(e: Pick<Entrega, 'status' | 'final_channel'>): { texto: string; problema: boolean } {
    const canal = e.final_channel === 'whatsapp' ? 'WhatsApp' : e.final_channel === 'instagram' ? 'Instagram' : e.final_channel === 'email' ? 'Email' : e.final_channel === 'telegram' ? 'Telegram' : null;
    const estado: Readonly<Record<Entrega['status'], string>> = {
        pending: 'preparando',
        queued: 'en cola',
        sent: 'enviado',
        delivered: 'entregado',
        read: 'leído',
        fallback: 'por el email de respaldo',
        failed: 'no ha llegado',
        discarded: 'no se envió',
    };
    const texto = estado[e.status];
    return { texto: canal === null ? texto : `${canal} · ${texto}`, problema: e.status === 'failed' || e.status === 'discarded' };
}

/** Qué hacer si el pase no ha llegado. */
export function consejoEnvio(e: Pick<Entrega, 'status' | 'final_channel'>): string | null {
    if (e.status !== 'failed') {
        return null;
    }
    return e.final_channel === 'email'
        ? 'El email ha rebotado. Comprueba con tu invitado que la dirección es correcta; si no lo es, retíralo y apúntalo de nuevo con la buena.'
        : 'No le ha llegado. Prueba a reenviarlo; si sigue sin llegar, pídele que te escriba por Instagram.';
}

/** Ventas: «2 entradas», «1 reservado», «1 invitado». */
export function unidadVenta(v: Pick<VentaAtribuida, 'source_kind' | 'quantity'>): string {
    const uno = v.quantity === 1;
    if (v.source_kind === 'reservation') {
        return uno ? 'reservado' : 'reservados';
    }
    if (v.source_kind === 'guest') {
        return uno ? 'invitado' : 'invitados';
    }
    return uno ? 'entrada' : 'entradas';
}

/** «EMB-7K2Q · General» (G788); sin referencia ni etiqueta, nada. */
export function lineaVenta(v: Pick<VentaAtribuida, 'reference' | 'label'>): string {
    return [v.reference, v.label].filter((x): x is string => typeof x === 'string' && x !== '').join(' · ');
}

export const ESTADO_COMISION: Readonly<Record<Comision['status'], readonly [string, EstadoChip]>> = {
    provisional: ['Estimada', 'pending'],
    pending: ['Por liquidar', 'neutral'],
    settled: ['Liquidada', 'confirmed'],
    reversed: ['Anulada', 'problem'],
};

export const BASE_COMISION: Readonly<Record<Comision['basis'], string>> = {
    guest_arrival: 'Por invitado que llega',
    ticket_sold: 'Por entrada vendida',
    ticket_arrival: 'Por entrada que llega',
    reservation: 'Por reservado',
};

/** «Del 1 al 30 sep». */
export function periodoLiquidacion(l: Pick<Liquidacion, 'period_from' | 'period_to'>): string {
    const f = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    return `Del ${f(l.period_from)} al ${f(l.period_to)}`;
}

/** Comprueba lo que se escribe al apuntar: nombre y uno de los dos, Instagram o email (G49; nunca teléfono). */
export function erroresAlta(d: { nombre: string; instagram: string; email: string; lista: string }, conInstagram: boolean): Partial<Record<'nombre' | 'contacto' | 'email' | 'lista', string>> {
    const e: Partial<Record<'nombre' | 'contacto' | 'email' | 'lista', string>> = {};
    if (d.lista === '') {
        e.lista = 'Elige la lista.';
    }
    if (d.nombre.trim().length < 2) {
        e.nombre = 'Escribe su nombre.';
    }
    const ig = conInstagram ? d.instagram.trim() : '';
    if (ig === '' && d.email.trim() === '') {
        e.contacto = conInstagram ? 'Escribe su Instagram o su email.' : 'Escribe su email.';
    }
    if (d.email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())) {
        e.email = 'Ese email no parece válido.';
    }
    return e;
}

/** Lo que dice el portal tras apuntar (`outcome` y `delivery` de addGuest). */
export function textoApuntado(r: { outcome: 'created' | 'already_registered'; delivery?: 'sent_by_email' | 'waiting_instagram_message' }): string {
    if (r.outcome === 'already_registered') {
        return 'Ya estaba apuntado a este evento. No se ha duplicado.';
    }
    return r.delivery === 'waiting_instagram_message' ? 'Apuntado. Mándale su enlace personal por Instagram: cuando te escriba con él, le llega el pase.' : 'Apuntado. Le hemos mandado el pase por email.';
}
